import * as React from "react";
import { useConvex, useMutation, useQuery } from "convex/react";
import { ChevronsUpDown, Download, History, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convexApi";
import { formatCurrency, formatDate, formatUtcDateOnly } from "@/lib/format";
import { downloadCsv } from "@/lib/quickbooks-export";

interface SelectableBillRow {
  _id: string;
  invoiceNumber?: string;
  vendorName?: string;
  totalAmount?: number;
  dueDate?: number;
  status: string;
}

interface PreviewRow {
  billId?: string;
  invoiceNumber?: string;
  payee?: string;
  invoiceDate?: number;
  dueDate?: number;
  totalAmount?: number;
  revision?: number;
  allocationCount: number;
  alreadyExported: boolean;
  errors: string[];
}

function BillChecklist({
  title,
  bills,
  selected,
  onToggle,
  onToggleAll,
}: {
  title: string;
  bills: SelectableBillRow[];
  selected: Set<string>;
  onToggle: (billId: string, next: boolean) => void;
  onToggleAll: (billIds: string[], next: boolean) => void;
}) {
  if (bills.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        No {title.toLowerCase()} bills.
      </p>
    );
  }
  const allIds = bills.map((bill) => bill._id);
  const selectedCount = allIds.filter((id) => selected.has(id)).length;
  const allSelected = selectedCount === bills.length;
  const totalAmount = bills.reduce(
    (total, bill) => total + (bill.totalAmount ?? 0),
    0,
  );
  return (
    <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border p-2">
      <label className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded border-b px-1.5 py-1 pb-1.5 text-sm font-medium">
        <Checkbox
          checked={allSelected || (selectedCount > 0 && "indeterminate")}
          onCheckedChange={(checked) => onToggleAll(allIds, checked === true)}
          aria-label={`Select all ${title.toLowerCase()} bills`}
        />
        <span className="min-w-0 flex-1">
          Select all — {bills.length} bill{bills.length === 1 ? "" : "s"}
        </span>
        <span className="tabular-nums">{formatCurrency(totalAmount)}</span>
      </label>
      {bills.map((bill) => {
        const label = bill.invoiceNumber ?? "(no invoice #)";
        return (
          <label
            key={bill._id}
            className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm"
          >
            <Checkbox
              checked={selected.has(bill._id)}
              onCheckedChange={(checked) =>
                onToggle(bill._id, checked === true)
              }
              aria-label={`Select bill ${label}`}
            />
            <span className="min-w-0 flex-1 truncate">
              {label}
              {bill.vendorName ? (
                <span className="text-muted-foreground"> — {bill.vendorName}</span>
              ) : null}
            </span>
            <span className="tabular-nums">
              {bill.totalAmount !== undefined
                ? formatCurrency(bill.totalAmount)
                : "—"}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function ExportBillsDialog({
  authUserId,
  open: controlledOpen,
  onOpenChange,
  initialSelectedIds,
}: {
  authUserId: string | undefined;
  /** Controlled mode: when provided, no trigger button is rendered. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Bill ids to pre-select when the dialog opens (bulk export from the inbox). */
  initialSelectedIds?: string[];
}) {
  const convex = useConvex();
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [profileId, setProfileId] = React.useState("");
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [includeExported, setIncludeExported] = React.useState(false);
  const [reExportReason, setReExportReason] = React.useState("");
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  const profiles = useQuery(
    api.accountingBillMasters.listExportProfiles,
    open && authUserId ? { authUserId } : "skip",
  ) as { _id: string; name: string; paymentAccount: string }[] | undefined;

  const approvedBills = useQuery(
    api.accountingBills.listBills,
    open && authUserId
      ? { authUserId, status: "approved", limit: 500 }
      : "skip",
  ) as SelectableBillRow[] | undefined;

  const exportedBills = useQuery(
    api.accountingBills.listBills,
    open && authUserId && includeExported
      ? { authUserId, status: "exported", limit: 500 }
      : "skip",
  ) as SelectableBillRow[] | undefined;

  // Remember every exported bill id we have shown so that turning re-export off
  // can drop them from the selection even if the query has since gone stale.
  const exportedIdsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    for (const bill of exportedBills ?? []) {
      exportedIdsRef.current.add(bill._id);
    }
  }, [exportedBills]);

  // Auto-select the only active profile so the common path is one click.
  React.useEffect(() => {
    if (!profileId && profiles && profiles.length === 1) {
      setProfileId(profiles[0]!._id);
    }
  }, [profiles, profileId]);

  const selectedIds = React.useMemo(
    () => Array.from(selected).sort(),
    [selected],
  );

  const preview = useQuery(
    api.accountingBillExport.getExportPreview,
    open && authUserId && profileId && selectedIds.length > 0
      ? {
          authUserId,
          billIds: selectedIds,
          profileId,
          allowReExport: includeExported,
        }
      : "skip",
  ) as
    | {
        rows: PreviewRow[];
        canExport: boolean;
        billCount: number;
        totalAmount: number;
        rowCount: number;
      }
    | undefined;

  const batches = useQuery(
    api.accountingBillExport.listExportBatches,
    open && historyOpen && authUserId ? { authUserId, limit: 25 } : "skip",
  ) as
    | {
        _id: string;
        filename: string;
        billCount: number;
        rowCount: number;
        totalAmount: number;
        reExportReason?: string;
        createdAt: number;
        profileSnapshot: { name: string };
      }[]
    | undefined;

  const createExportBatch = useMutation(
    api.accountingBillExport.createExportBatch,
  );

  // Seed the selection from the inbox's bulk-selected rows on open.
  React.useEffect(() => {
    if (open && initialSelectedIds && initialSelectedIds.length > 0) {
      setSelected(new Set(initialSelectedIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleBill = (billId: string, next: boolean) => {
    setSelected((prev) => {
      const draft = new Set(prev);
      if (next) draft.add(billId);
      else draft.delete(billId);
      return draft;
    });
  };

  const toggleAllBills = (billIds: string[], next: boolean) => {
    setSelected((prev) => {
      const draft = new Set(prev);
      for (const id of billIds) {
        if (next) draft.add(id);
        else draft.delete(id);
      }
      return draft;
    });
  };

  const resetState = () => {
    setSelected(new Set());
    setIncludeExported(false);
    setReExportReason("");
    setHistoryOpen(false);
  };

  const needsReason = includeExported && !reExportReason.trim();
  const canDownload =
    !!authUserId &&
    !!profileId &&
    selectedIds.length > 0 &&
    preview !== undefined &&
    preview.canExport &&
    !needsReason &&
    !isExporting;

  const handleExport = async () => {
    if (!authUserId || !profileId || selectedIds.length === 0) return;
    setIsExporting(true);
    try {
      const result = await createExportBatch({
        authUserId,
        billIds: selectedIds,
        profileId,
        reExportReason: includeExported
          ? reExportReason.trim() || undefined
          : undefined,
      });
      downloadCsv(result.csv, result.filename);
      toast.success(
        `Exported ${selectedIds.length} bill${selectedIds.length === 1 ? "" : "s"} — ${result.filename}`,
      );
      setSelected(new Set());
      setReExportReason("");
      setIncludeExported(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleRedownload = async (batchId: string) => {
    if (!authUserId) return;
    try {
      const result = await convex.query(api.accountingBillExport.getBatchCsv, {
        authUserId,
        batchId,
      });
      downloadCsv(result.csv, result.filename);
      toast.success(`Downloaded ${result.filename}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not rebuild the batch CSV",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetState();
      }}
    >
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" disabled={!authUserId}>
            <Download className="mr-2 h-4 w-4" />
            Export to QBO
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Export bills to QuickBooks</DialogTitle>
          <DialogDescription>
            Download approved bills as a strict QBO bills-import CSV. Every
            selected bill must be valid; a single blocker stops the batch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="export-profile">Export profile</Label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger id="export-profile">
                <SelectValue placeholder="Select export profile" />
              </SelectTrigger>
              <SelectContent>
                {(profiles ?? []).map((profile) => (
                  <SelectItem key={profile._id} value={profile._id}>
                    {profile.name} — {profile.paymentAccount}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {profiles !== undefined && profiles.length === 0 && (
              <p className="text-destructive text-xs">
                No active export profiles. Create one in Bills settings first.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Approved bills</Label>
            {approvedBills === undefined ? (
              <p className="text-muted-foreground text-xs">Loading…</p>
            ) : (
              <BillChecklist
                title="Approved"
                bills={approvedBills}
                selected={selected}
                onToggle={toggleBill}
                onToggleAll={toggleAllBills}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={includeExported}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setIncludeExported(next);
                  if (!next) {
                    setReExportReason("");
                    // Drop hidden exported bills so the preview does not choke
                    // on an "already exported" bill the user can no longer see.
                    setSelected((prev) => {
                      const draft = new Set(prev);
                      for (const id of exportedIdsRef.current) draft.delete(id);
                      return draft;
                    });
                  }
                }}
              />
              Include already-exported bills (re-export)
            </label>
            {includeExported && (
              <div className="space-y-1.5 pl-6">
                {exportedBills === undefined ? (
                  <p className="text-muted-foreground text-xs">Loading…</p>
                ) : (
                  <BillChecklist
                    title="Exported"
                    bills={exportedBills}
                    selected={selected}
                    onToggle={toggleBill}
                    onToggleAll={toggleAllBills}
                  />
                )}
                <Textarea
                  value={reExportReason}
                  onChange={(e) => setReExportReason(e.target.value)}
                  placeholder="Reason for re-exporting (required)"
                  aria-label="Reason for re-exporting"
                  rows={2}
                />
              </div>
            )}
          </div>

          {preview && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  Preview — {preview.billCount} bill
                  {preview.billCount === 1 ? "" : "s"}, {preview.rowCount} CSV
                  row{preview.rowCount === 1 ? "" : "s"}
                </span>
                <span className="tabular-nums">
                  {formatCurrency(preview.totalAmount)}
                </span>
              </div>
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {preview.rows.map((row, index) => (
                  <div
                    key={row.billId ?? index}
                    className="border-b pb-1 text-xs last:border-b-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate">
                        {row.invoiceNumber ?? "(no invoice #)"}
                        {row.payee ? ` — ${row.payee}` : ""}
                        {row.alreadyExported ? " (re-export)" : ""}
                      </span>
                      <span className="tabular-nums">
                        {row.totalAmount !== undefined
                          ? formatCurrency(row.totalAmount)
                          : "—"}
                        {row.dueDate !== undefined
                          ? ` · due ${formatUtcDateOnly(row.dueDate, { month: "short" })}`
                          : ""}
                      </span>
                    </div>
                    {row.errors.length > 0 && (
                      <ul className="text-destructive mt-0.5 list-disc pl-4">
                        {row.errors.map((error, errorIndex) => (
                          <li key={errorIndex}>{error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="px-2">
                <History className="mr-2 h-4 w-4" />
                Export history
                <ChevronsUpDown className="ml-1 h-3 w-3" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {batches === undefined ? (
                <p className="text-muted-foreground p-2 text-xs">Loading…</p>
              ) : batches.length === 0 ? (
                <p className="text-muted-foreground p-2 text-xs">
                  No export batches yet.
                </p>
              ) : (
                <div className="max-h-48 space-y-1 overflow-y-auto p-1">
                  {batches.map((batch) => (
                    <div
                      key={batch._id}
                      className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{batch.filename}</p>
                        <p className="text-muted-foreground">
                          {formatDate(batch.createdAt, { month: "short" })} ·{" "}
                          {batch.billCount} bill
                          {batch.billCount === 1 ? "" : "s"} ·{" "}
                          {formatCurrency(batch.totalAmount)} ·{" "}
                          {batch.profileSnapshot.name}
                          {batch.reExportReason ? " · re-export" : ""}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={`Re-download ${batch.filename}`}
                        onClick={() => handleRedownload(batch._id)}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button onClick={handleExport} disabled={!canDownload}>
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
